/**
 * WB4 Phase 3 — Palette intent approval: timeline (ai_suggested → user_approved) + ActionExecutor path.
 */

import type { NavigateFunction } from 'react-router-dom';
import type { ResolvedCommand } from '@shared/business-workbench/command-resolver';
import type { PaletteIntentProposal } from '@shared/business-workbench/workbench-palette-intent-proposal';
import {
  buildPaletteIntentAiSuggestedTimelineEvent,
  buildPaletteIntentUserApprovedTimelineEvent,
} from '@shared/business-workbench/workbench-palette-intent-timeline';
import {
  resolveWorkbenchTimelineObjectRef,
  buildWorkbenchTimelineEventId,
} from '@shared/business-workbench/workbench-timeline-event';
import { appendWorkbenchTimelineEventOutboxFirst } from '../services/workbenchTimelineApi';
import { emitWorkbenchTimelineInvalidated } from '../workbench/workbench-timeline-events';
import {
  executeWorkbenchCommand,
  type WorkbenchCommandExecuteOptions,
} from './globalSearchCommands';
import { getCurrentUser } from './jwtUtils';
import { logger } from '@shared/logger';

export interface ApprovePaletteIntentProposalInput {
  tenantId: string;
  proposal: PaletteIntentProposal;
  navigate: NavigateFunction;
  executeOptions?: WorkbenchCommandExecuteOptions;
  businessDomain?: string;
  objectId?: string;
}

function resolveTimelineActor(): { userId: string; displayName?: string } {
  const user = getCurrentUser();
  if (!user?.userId) {
    return { userId: 'palette-user' };
  }
  return {
    userId: user.userId,
    ...(user.role ? { role: user.role } : {}),
  };
}

async function appendPaletteIntentTimelineEvent(
  input: Parameters<typeof buildPaletteIntentAiSuggestedTimelineEvent>[0]
): Promise<void> {
  try {
    const event = await appendWorkbenchTimelineEventOutboxFirst(
      buildPaletteIntentAiSuggestedTimelineEvent(input),
      { auditRequired: true }
    );
    if (event) {
      emitWorkbenchTimelineInvalidated({
        domain: event.domain,
        objectType: event.objectType,
        objectId: event.objectId,
      });
    }
  } catch (error) {
    logger.warn('palette intent ai_suggested timeline append failed', { error });
  }
}

export async function recordPaletteIntentAiSuggested(
  input: Omit<ApprovePaletteIntentProposalInput, 'navigate' | 'executeOptions'>
): Promise<void> {
  const objectId = input.objectId ?? input.proposal.payload.objectId ?? 'palette';
  const timelineRef = resolveWorkbenchTimelineObjectRef({
    objectId,
    ...(input.businessDomain ? { businessDomain: input.businessDomain } : {}),
  });

  await appendPaletteIntentTimelineEvent({
    tenantId: input.tenantId,
    domain: timelineRef.domain,
    objectType: timelineRef.objectType,
    objectId: timelineRef.objectId,
    intent: input.proposal.intent,
    actionId: input.proposal.command?.handlerRef ?? input.proposal.intent,
    actionLabel: input.proposal.label,
    riskTier: input.proposal.riskTier,
    actor: resolveTimelineActor(),
    interpretationSummary: input.proposal.interpretationSummary,
    id: buildWorkbenchTimelineEventId('wtl-ai'),
  });
}

export async function approvePaletteIntentProposal(
  input: ApprovePaletteIntentProposalInput
): Promise<'executed' | 'noop' | 'clarification_required'> {
  if (input.proposal.requiresClarification || !input.proposal.command) {
    return 'clarification_required';
  }

  const command: ResolvedCommand = input.proposal.command;
  const objectId = input.objectId ?? input.proposal.payload.objectId ?? 'palette';
  const timelineRef = resolveWorkbenchTimelineObjectRef({
    objectId,
    ...(input.businessDomain ? { businessDomain: input.businessDomain } : {}),
  });

  try {
    await appendWorkbenchTimelineEventOutboxFirst(
      buildPaletteIntentUserApprovedTimelineEvent({
        tenantId: input.tenantId,
        domain: timelineRef.domain,
        objectType: timelineRef.objectType,
        objectId: timelineRef.objectId,
        intent: input.proposal.intent,
        actionId: command.handlerRef ?? command.id,
        actionLabel: command.label,
        riskTier: input.proposal.riskTier,
        actor: resolveTimelineActor(),
        interpretationSummary: input.proposal.interpretationSummary,
        id: buildWorkbenchTimelineEventId('wtl-approve'),
      }),
      { auditRequired: true }
    );
    emitWorkbenchTimelineInvalidated(timelineRef);
  } catch (error) {
    logger.warn('palette intent user_approved timeline append failed', { error });
  }

  const outcome = executeWorkbenchCommand(command, input.navigate, input.executeOptions ?? {});
  return outcome === 'executed' || outcome === 'navigated' ? 'executed' : 'noop';
}
